import $ from 'jquery';

import KeyUtils from 'chairisher/util/key';
import SocialShare from 'chairisher/view/socialshare';

import { isMaxPhone } from 'chairisher/util/mediaquery';

/**
 * Allows you to view a business image full screen
 *
 * @param {Object} options The object containing the options for the business image viewer
 * @param {jQuery} options.$cards Cards viewed in this viewer
 * @param {string=} options.viewerTemplateSelector The selector to use to find the viewer template in the DOM
 */
class BusinessImageViewer {
    constructor(options = {}) {
        this.$cards = options.$cards ?? null;
        this.viewerTemplateSelector = options.viewerTemplateSelector ?? '#js-card-viewer-template';
        this.$viewerEl = $($(this.viewerTemplateSelector).html());
        $(document.body).append(this.$viewerEl);

        this.currentIndex = null;
        this.dataEndpointUrl = null;
        this.eventNamespace = 'businessimageviewer';
        this.nextUrl = null;
        this.previousUrl = null;
    }

    /**
     * @returns {jQuery} The jQuery element holding the main viewer element
     */
    getViewerEl() {
        return this.$viewerEl;
    }

    /**
     * Hides the viewer
     */
    hide() {
        this.$viewerEl.addClass('hidden');

        // TODO: move this to the constructor or bind method
        // show live chat
        $('#launcher').css('visibility', 'visible');
    }

    /**
     * Shows the business image viewer
     *
     * @param {string} dataEndpointUrl The endpoint to hit for the image to show
     * @param {string|null} previousUrl The endpoint to hit for the previous image (if any)
     * @param {string|null} nextUrl The endpoint to hit for the next image (if any)
     */
    show(dataEndpointUrl, previousUrl, nextUrl) {
        this.dataEndpointUrl = dataEndpointUrl;
        this.nextUrl = nextUrl;
        this.previousUrl = previousUrl;

        this.fetchData().done((data) => {
            this.$viewerEl.find('.js-viewer-content').html(data.html);

            this.$nextEl = this.$viewerEl.find('.js-next');
            this.$prevEl = this.$viewerEl.find('.js-previous');

            this.toggleNextAction(this.nextUrl === null);
            this.togglePreviousAction(this.previousUrl === null);

            SocialShare.bind();

            this.$viewerEl.removeClass('hidden');

            const trunatedDescriptionMessage = this.$viewerEl.find('.js-description-condensed').text();
            const fullDescriptionMessage = this.$viewerEl.find('.js-description-full').text();
            if (fullDescriptionMessage.length <= trunatedDescriptionMessage.length || !isMaxPhone()) {
                this.$viewerEl.find('.js-description-full').toggleClass('hidden');
                this.$viewerEl.find('.js-description-condensed').toggleClass('hidden');
                this.$viewerEl.find('.js-description-toggle').toggleClass('hidden');
            }

            // hide live chat
            $('#launcher').css('visibility', 'hidden');
        });
    }

    /**
     * Toggles the "next" action between being enabled and disabled
     *
     * @param {boolean} shouldDisable
     */
    toggleNextAction(shouldDisable) {
        this.$nextEl.toggleClass('disabled', shouldDisable).prop('disabled', shouldDisable);
    }

    /**
     * Toggles the "previous" action between being enabled and disabled
     *
     * @param {boolean} shouldDisable
     */
    togglePreviousAction(shouldDisable) {
        this.$prevEl.toggleClass('disabled', shouldDisable).prop('disabled', shouldDisable);
    }

    /**
     * Binds actions to the viewer element
     */
    bind() {
        this.$viewerEl.on('click', '.js-card-link', (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        this.$viewerEl.on('click', '.js-close', (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.hide();
        });

        $(document).on('keyup.viewer', (e) => {
            if (e.which === KeyUtils.KeyCodes.Escape) {
                this.hide();
            }
        });

        this.$viewerEl.on('click', '.js-next:not(.disabled), .js-previous:not(.disabled)', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isNext = $(e.currentTarget).hasClass('js-next');
            this.currentIndex += isNext ? 1 : -1;
            const urls = this.getUrlsToShow();

            this.show(urls.url, urls.previousCardUrl, urls.nextCardUrl);
        });

        $('.js-card-link').on('click', (e) => {
            e.preventDefault();

            const $link = $(e.currentTarget);
            const $card = $link.closest('.js-card-image');
            this.currentIndex = $card.index(); // position in the grid

            const urls = this.getUrlsToShow();

            this.show(urls.url, urls.previousCardUrl, urls.nextCardUrl);
        });
    }

    /**
     * @returns {$.Deferred} Ajax request that fetches data to display
     */
    fetchData() {
        return $.ajax({
            url: this.dataEndpointUrl,
        });
    }

    /**
     * @returns {Object}
     */
    getUrlsToShow() {
        const url = this.$cards.eq(this.currentIndex).find('.js-card-link').attr('href');
        let nextCardUrl = null;
        let previousCardUrl = null;

        if (this.currentIndex !== 0) {
            previousCardUrl = this.$cards
                .eq(this.currentIndex - 1)
                .find('.js-card-link')
                .attr('href');
        }

        if (this.currentIndex !== this.$cards.length - 1) {
            nextCardUrl = this.$cards
                .eq(this.currentIndex + 1)
                .find('.js-card-link')
                .attr('href');
        }

        return {
            url,
            nextCardUrl,
            previousCardUrl,
        };
    }
}

export default BusinessImageViewer;
